import React from "react";
import { Link, NavLink } from "react-router-dom";
import { ChevronRight, FlaskConical, Wand2, Wrench, UserCircle2, Bookmark, Beaker } from "lucide-react";
import { cn } from "@/lib/utils";

const NAV = [
  { to: "/ai-cpg-tools/prompt-builder", label: "Prompt Builder", icon: Wand2 },
  { to: "/ai-cpg-tools/tools", label: "AI Tools", icon: Wrench },
  { to: "/ai-cpg-tools/formulation-creator", label: "Formulation", icon: Beaker },
  { to: "/ai-cpg-tools/saved", label: "My Prompts", icon: Bookmark },
];

/**
 * AILabShell — shared frame for every AI CPG Tools page: sub-nav + breadcrumb + max-width container.
 * Drop it inside your app's existing authenticated layout (sidebar/header) — it does not add its own.
 *
 * props
 *   crumbs   [{ label, to? }]  — breadcrumb trail after "AI CPG Tools"
 *   title, subtitle, actions (ReactNode rendered right of the title)
 */
export default function AILabShell({ crumbs = [], title, subtitle, actions, children, wide = false }) {
  return (
    <div className="min-h-full bg-background">
      <div
        className="sticky z-30 border-b bg-card/80 backdrop-blur supports-[backdrop-filter]:bg-card/60"
        style={{ top: "var(--header-h, 64px)" }}
      >
        <div className="w-full flex items-center gap-2 overflow-x-auto px-4 py-2 sm:px-6">
          {/* Brand Profile — pinned as the first tab per product spec.
              Everyone comes here first before running any generator so
              the AI has brand context to work with. */}
          <NavLink
            to="/ai-cpg-tools/brand-profile"
            className={({ isActive }) =>
              cn("inline-flex shrink-0 items-center gap-1.5 rounded-md px-2.5 py-1.5 text-sm text-muted-foreground hover:bg-muted hover:text-foreground", isActive && "bg-muted text-foreground")
            }
          >
            <UserCircle2 className="h-4 w-4" /> <span className="hidden sm:inline">Brand Profile</span>
          </NavLink>
          <span className="mx-1 h-4 w-px shrink-0 bg-border" />
          {/* Home link — routes to the AI CPG Tools dashboard. If an
              admin hides `nav-ai-cpg-tools` in the Menu Directory, the
              MenuGate wrapping this whole surface will intercept the
              hit and redirect to /welcome, so this link is safe to
              leave pointing at the natural home. */}
          <Link to="/ai-cpg-tools" className="inline-flex shrink-0 items-center gap-2 rounded-md px-2 py-1.5 text-sm font-semibold hover:bg-muted" title="AI CPG Tools home">
            <FlaskConical className="h-4 w-4 text-violet-600" /> AI CPG Tools
          </Link>
          <span className="mx-1 h-4 w-px shrink-0 bg-border" />
          <nav className="flex items-center gap-1" aria-label="AI CPG Tools">
            {NAV.map(({ to, label, icon: Icon }) => (
              <NavLink
                key={to}
                to={to}
                className={({ isActive }) =>
                  cn("inline-flex shrink-0 items-center gap-1.5 rounded-md px-2.5 py-1.5 text-sm text-muted-foreground hover:bg-muted hover:text-foreground", isActive && "bg-muted text-foreground")
                }
              >
                <Icon className="h-4 w-4" /> <span className="hidden sm:inline">{label}</span>
              </NavLink>
            ))}
          </nav>
        </div>
      </div>

      <div className="w-full px-4 py-6 sm:px-6 sm:py-8">
        {/* Breadcrumb trail — starts at Welcome, followed by the AI CPG
            Tools home. If the Menu Directory hides `nav-ai-cpg-tools`,
            the MenuGate wrapping this surface intercepts the hit and
            redirects users to /welcome, so the "AI CPG Tools" crumb
            here can safely point at the natural home. */}
        <nav className="mb-3 flex flex-wrap items-center gap-1 text-xs text-muted-foreground" aria-label="Breadcrumb">
          <Link to="/welcome" className="hover:text-foreground" data-testid="ai-lab-crumb-welcome">Welcome</Link>
          <ChevronRight className="h-3 w-3" />
          {crumbs.length > 0 ? (
            <Link to="/ai-cpg-tools" className="hover:text-foreground" data-testid="ai-lab-crumb-home">AI CPG Tools</Link>
          ) : (
            <span className="text-foreground" data-testid="ai-lab-crumb-home">AI CPG Tools</span>
          )}
          {crumbs.map((c, i) => (
            <React.Fragment key={i}>
              <ChevronRight className="h-3 w-3" />
              {c.to ? <Link to={c.to} className="hover:text-foreground">{c.label}</Link> : <span className="text-foreground">{c.label}</span>}
            </React.Fragment>
          ))}
        </nav>
        {(title || actions) && (
          <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div className="min-w-0">
              {title && <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">{title}</h1>}
              {subtitle && <p className="mt-1 max-w-2xl text-sm text-muted-foreground sm:text-base">{subtitle}</p>}
            </div>
            {actions && <div className="flex shrink-0 flex-wrap gap-2">{actions}</div>}
          </div>
        )}
        {children}
      </div>
    </div>
  );
}
